import React, { useState } from 'react';
import {
  Box,
  GridLegacy as Grid,
  Typography,
  Collapse,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import globeImage from './ink-link.jpg';

const collapsibles = [
  {
    title: 'Professional Profile & Networking',
    content:
      'Craft a comprehensive talent profile: design galleries and collections to feature selected projects, commissions, or client deliverables. Attach your resume, personal links, and credentials. The platform promotes connectivity with prospective clients, employers, and creative peers, including a channel to receive commission or collaboration inquiries.',
  },
  {
    title: 'Audience Growth & International Visibility',
    content:
      'Expand your reach and cultivate an international audience—an essential step in establishing a professional presence as a writer or artist. Increased exposure can lead to more inquiries for commissions, collaborations, and professional opportunities. A well-developed online presence also contributes to professional credibility.',
  },
  {
    title: 'Build a Formal Digital Portfolio',
    content:
      "Design a sleek, professional-grade portfolio with a custom shareable link—ideal for applications to art programs, internships, publishing opportunities, and professional outreach. It provides a centralized, distributable and distinguished way to present your work without bearing the costs of launching an independent website."
  },
  {
    title: 'Become Verified & Highlight Publications',
    content:
      'Display your formal publications and verified status on your profile to secure professional recognition and signal distinction to peers, employers, and academic institutions.'
  },
];

const FeatureInkLink: React.FC = () => {
  const [hovered, setHovered] = useState<number | null>(null);
  const [clicked, setClicked] = useState<number | null>(null);

  return (
    <Box sx={{ 
      px: { xs: 2, sm: 4, md: 8, xl: 16 }, 
      py: { xs: 4, md: 8 }, 
      backgroundColor: 'transparent' 
    }}>
      <Grid container spacing={{ xs: 2, md: 4 }} alignItems="stretch">
        {/* Left Text Section */}
        <Grid item xs={12} md={6}>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 600,
              fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
              lineHeight: { xs: 1.2, md: 1.3 }
            }}
          >
            InkLink Network
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              mt: { xs: 1.5, md: 2 }, 
              fontWeight: 'bold',
              fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.5rem' },
              lineHeight: { xs: 1.3, md: 1.4 },
              width: '100%',
            }}
          >
            A global stage for student authors & artists
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              fontSize: { xs: '1rem', md: '1.1rem' }, 
              mb: { xs: 2, md: 3 },
              lineHeight: { xs: 1.4, md: 1.5 },
              width: '100%',
              maxWidth: '65ch',
            }}
          >
            Elevate your professional profile on our multimedia social network. <br />
            Showcase, curate a formal portfolio, and forge connections.
          </Typography>

          <Typography 
            variant="body2" 
            sx={{ 
              mb: { xs: 1.5, md: 2 },
              fontSize: { xs: '0.9rem', md: '1rem' },
              lineHeight: { xs: 1.4, md: 1.5 },
              maxWidth: { xs: '65ch', md: '65ch' }
            }}
          >
            InkLink is a professional and social network for emerging writers and artists. The network is designed to help students gain visibility, build a polished public profile, open avenues of networking, and showcase their work. Unlike mainstream platforms—where algorithmic priorities may limit exposure—InkLink ensures that creative work is not buried but spotlighted.
          </Typography>

          <Typography 
            variant="body2" 
            sx={{ 
              mb: { xs: 2, md: 3 },
              fontSize: { xs: '0.9rem', md: '1rem' },
              lineHeight: { xs: 1.4, md: 1.5 },
              maxWidth: { xs: '65ch', md: '65ch' }
            }}
          >
            We empower students to capitalize on their talents, which serve as a gateway to numerous career paths, and to position themselves as professionals from the start.
          </Typography>

          {/* Collapsible Feature List */}
          {collapsibles.map((item, idx) => {
            const isOpen = clicked === idx || hovered === idx; // Open on click or hover
            return (
              <Box
                key={idx}
                onMouseEnter={() => setHovered(idx)} // Desktop hover
                onMouseLeave={() => setHovered(null)} // Desktop hover
                onClick={() => {
                  // Mobile/Desktop click toggle
                  if (clicked === idx) {
                    setClicked(null); // Close if already open
                  } else {
                    setClicked(idx); // Open this one
                  }
                }}
                sx={{ 
                  mt: { xs: 1.5, md: 2 }, 
                  pb: 1, 
                  cursor: 'pointer' 
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  minHeight: { xs: 44, md: 'auto' }, // Minimum touch target size
                }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 'bold',
                      fontSize: { xs: '0.95rem', md: '1rem' },
                      lineHeight: { xs: 1.3, md: 1.4 },
                      maxWidth: { xs: '80%', md: '85%' } // Prevent text from overlapping button
                    }}
                  >
                    {item.title}
                  </Typography>
                  <IconButton
                    size="small"
                    disableRipple
                    sx={{
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease-in-out',
                      minWidth: { xs: 44, md: 'auto' }, // Minimum touch target size
                      minHeight: { xs: 44, md: 'auto' },
                    }}
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                </Box>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mt: 1,
                      fontSize: { xs: '0.85rem', md: '0.9rem' },
                      lineHeight: { xs: 1.4, md: 1.5 },
                      maxWidth: { xs: '100%', md: '95%' }
                    }}
                  >
                    {item.content}
                  </Typography>
                </Collapse>
              </Box>
            );
          })}
        </Grid>

        {/* Right Image Section */}
				<Grid item xs={12} md={6} sx={{ order: { xs: -1, md: 0 } }}>
					<Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",

                        height: {
                            xs: '250px', // Fixed height for mobile
                            sm: '400px',
                            md: '500px',
                            lg: '600px',
                        },

                        width: '100%',
                        mt: { xs: 0, md: 0 },
                        mb: { xs: 2, md: 0 },
                        overflow: 'hidden',
                      }}
                    >
						<Box
							component="img"
							src={globeImage}
							alt="image"
							sx={{
								width: '100%',
								height: '100%',
								objectFit: 'cover',
								borderRadius: { xs: 1, md: 2 },
								boxShadow: { xs: 2, md: 3 },
							}}
						/>
					</Box>
				</Grid>
      </Grid>
    </Box>
  );
};

export default FeatureInkLink;
